import React, { useEffect } from 'react';
import {
  Box,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Snackbar,
  TextField,
  Stack,
  FormControlLabel,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import Modal from '@mui/material/Modal';
import InsightsLoader from '../../InsightsLoader';
import { Select } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import surveyTagService from '../../../../services/api/insights/surveyTagService';
import { AppButton } from '../../../../resources/styling';
import { colors } from '../../../../resources/theme/colors';
import {
  OutlineButton,
  TableStyle,
} from '../../../../resources/styling/appStyle';
import { surveyTemplateService } from '../../../../services/api/insights/surveyTemplateService';
import surveyTemplateQuestionsService from '../../../../services/api/insights/surveyTemplateQuestionsService';
import QuestionBankList from './QuestionBankList';
import { DeleteOutline } from '@mui/icons-material';
import surveyQuestionService from '../../../../services/api/insights/surveyQuestionService';
import Checkbox from '@mui/material/Checkbox';

const { mandy, pictonBlue } = colors;

const STAFF = { value: 1, label: 'Staff' };
const PARENT = { value: 2, label: 'Parent' };
const PUPIL = { value: 4, label: 'Pupil' };
const audiences = [STAFF, PARENT, PUPIL];

export default function AddEditSurvey({ doneEdit, template, updateAfterEdit }) {
  const [tags, setTags] = React.useState([]);
  const [id, setId] = React.useState(null);
  const [name, setName] = React.useState('');
  const [selectedAudiences, setSelectedAudiences] = React.useState([]);
  const [selectedTags, setSelectedTags] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [processing, setProcessing] = React.useState(false);
  const [questions, setQuestions] = React.useState([]);
  const [loadingQuestions, setLoadingQuestions] = React.useState(false);
  const [showQuestionBank, setShowQuestionBank] = React.useState(false);
  const [isCheckIn, setIsCheckIn] = React.useState(false);

  const [status, setStatus] = React.useState({
    open: false,
    message: '',
    type: 'success',
  });

  useEffect(() => {
    setLoading(true);
    surveyTagService
      .getTags()
      .then((res) => {
        setTags(res.sort((x, y) => x.tag.localeCompare(y.tag)));
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setLoading(true);
    if (template) {
      setLoadingQuestions(true);
      setId(template.id);
      setName(template.name);
      setIsCheckIn(template.isCheckInSurvey);
      if (template.audience === 1) setSelectedAudiences([STAFF]);
      if (template.audience === 2) setSelectedAudiences([PARENT]);
      if (template.audience === 4) setSelectedAudiences([PUPIL]);
      if (template.audience === 3) setSelectedAudiences([STAFF, PARENT]);
      if (template.audience === 5) setSelectedAudiences([PUPIL, STAFF]);
      if (template.audience === 6) setSelectedAudiences([PARENT, PUPIL]);
      if (template.audience === 7) setSelectedAudiences([STAFF, PARENT, PUPIL]);
      setSelectedTags(template.surveysTags);
      surveyTemplateQuestionsService
        .get(template.id)
        .then((res) => {
          setQuestions(res);
        })
        .finally(() => {
          setLoadingQuestions(false);
        });
      setLoading(false);
    } else {
      setId(0);
      setName(null);
      setSelectedAudiences([]);
      setSelectedTags([]);
      setLoading(false);
    }
  }, [template]);

  const renderQuestionBank = () =>
    showQuestionBank && (
      <QuestionBankList
        onClose={setShowQuestionBank}
        template={template}
        updateQuestionList={updateQuestionList}
      />
    );

  const updateQuestionList = () => {
    setLoadingQuestions(true);
    surveyTemplateQuestionsService
      .get(template.id)
      .then((res) => {
        setQuestions(res);
      })
      .finally(() => {
        setLoadingQuestions(false);
      });
  };
  const removeQuestion = (id) => {
    setLoadingQuestions(true);
    surveyQuestionService
      .delete(template.id, id)
      .then((res) => {
        if (res) {
          const newQuestions = questions.filter(
            (question) => question.id !== id
          );
          setQuestions([...newQuestions]);
        } else {
          setStatus({
            open: true,
            message: 'Error deleting question',
            type: 'error',
          });
        }
      })
      .finally(() => {
        setLoadingQuestions(false);
      });
  };

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        variant='filled'
        open={status.open}
        autoHideDuration={5000}
        onClose={() => setStatus({ ...status, open: false })}
      >
        <Alert severity={status.type} sx={{ width: '100%' }} variant='filled'>
          {status.message}
        </Alert>
      </Snackbar>
      <Box
        sx={{
          p: 3,
        }}
      >
        {loading ? (
          <InsightsLoader />
        ) : (
          <Modal open={true} onClose={() => doneEdit(false)}>
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '800px',
                transform: 'translate(-50%, -50%)',
                bgcolor: 'background.paper',
                p: 3,
              }}
            >
              <Grid container spacing={2} mb={2}>
                <Grid item xs={12} lg={12} md={12} sm={12}>
                  <h3>{id === 0 ? 'Add' : 'Edit'} Survey Template</h3>
                </Grid>
                <Grid item xs={12} lg={12} md={12} sm={12}>
                  <TextField
                    fullWidth
                    label='Template Name'
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} lg={12} md={12} sm={12}>
                  <FormControl fullWidth>
                    <InputLabel id='audience-chip-label'>Audience</InputLabel>
                    <Select
                      fullWidth
                      multiple
                      labelId='audience-chip-label'
                      label='Audience'
                      value={selectedAudiences}
                      onChange={(e) => setSelectedAudiences(e.target.value)}
                      renderValue={(selected) =>
                        selected.map((audience) => {
                          const audienceObj = audiences.find(
                            (aud) => aud.value === audience.value
                          );
                          return (
                            audienceObj?.label && (
                              <Chip
                                label={audienceObj?.label}
                                style={{ marginRight: 5 }}
                              />
                            )
                          );
                        })
                      }
                    >
                      {audiences.map((audience) => (
                        <MenuItem key={audience.value} value={audience}>
                          {audience.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} lg={12} md={12} sm={12}>
                  <FormControl fullWidth>
                    <InputLabel id='tag-chip-label'>Tags</InputLabel>
                    <Select
                      labelId='tag-chip-label'
                      label='Tags'
                      multiple
                      value={selectedTags}
                      onChange={(e) => {
                        if (e.target.value.length <= 3) {
                          setSelectedTags(e.target.value);
                        }
                      }}
                      renderValue={(selected) => {
                        return selected?.map((tag) => {
                          const tagObj = tags.find((t) => t.tag === tag);
                          return (
                            tagObj?.tag && (
                              <Chip
                                label={tagObj?.tag}
                                style={{ marginRight: 5 }}
                              />
                            )
                          );
                        });
                      }}
                    >
                      {tags.map((tag) => (
                        <MenuItem key={tag.id} value={tag.tag}>
                          {tag.tag}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} lg={12} md={12} sm={12}>
                  <FormControlLabel
                    label='Is Check In'
                    control={
                      <Checkbox
                        checked={isCheckIn}
                        onChange={(e) => setIsCheckIn(e.target.checked)}
                        sx={{ marginLeft: '10px' }}
                      />
                    }
                  />
                </Grid>
              </Grid>

              {renderQuestionBank()}
              {id !== 0 && (
                <Grid container spacing={2} mb={2}>
                  <Grid item xs={12} lg={12} md={12} sm={12}>
                    <Stack
                      direction='row'
                      spacing={1}
                      justifyContent='space-between'
                      alignItems='center'
                    >
                      <h3>Questions</h3>
                      <AppButton
                        color={mandy}
                        onClick={(e) => {
                          setShowQuestionBank(true);
                        }}
                      >
                        Add Questions
                      </AppButton>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} lg={12} md={12} sm={12}>
                    {loadingQuestions ? (
                      <InsightsLoader />
                    ) : questions.length > 0 ? (
                      <div className='bank-questions'>
                        <Grid container mb={2}>
                          <Grid item xs={12} lg={12} md={12} sm={12}>
                            <div className='participation-breakdown '>
                              <TableStyle>
                                <table>
                                  <thead>
                                    <tr>
                                      <th>Question</th>
                                      <th>Action</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {questions.map((question, index) => (
                                      <tr key={index}>
                                        <td>
                                          {question.staffText
                                            ? question.staffText
                                            : question.parentText
                                            ? question.parentText
                                            : question.pupilText}
                                        </td>
                                        <td>
                                          <IconButton
                                            sx={{ padding: '0!important' }}
                                            disableRipple
                                            onClick={() => {
                                              removeQuestion(question.id);
                                            }}
                                          >
                                            <DeleteOutline
                                              sx={{ fontSize: '22px' }}
                                            ></DeleteOutline>
                                          </IconButton>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </TableStyle>
                            </div>
                          </Grid>
                        </Grid>
                      </div>
                    ) : (
                      <div>No questions found</div>
                    )}
                  </Grid>
                </Grid>
              )}

              <Grid container>
                <Grid item xs={12} lg={12} md={12} sm={12}>
                  <Stack direction='row' spacing={1} justifyContent='flex-end'>
                    <OutlineButton
                      color={mandy}
                      onClick={(e) => doneEdit(false)}
                    >
                      Close
                    </OutlineButton>{' '}
                    <AppButton
                      disabled={processing}
                      color={mandy}
                      onClick={(e) => {
                        if (name === '' || name === null) {
                          setStatus({
                            open: true,
                            message: 'Please enter a template name',
                            type: 'error',
                          });
                          return;
                        }
                        if (selectedAudiences.length === 0) {
                          setStatus({
                            open: true,
                            message: 'Please select at least one audience',
                            type: 'error',
                          });
                          return;
                        }
                        if (selectedTags.length === 0) {
                          setStatus({
                            open: true,
                            message: 'Please select at least one tag',
                            type: 'error',
                          });
                          return;
                        }
                        setProcessing(true);
                        let aud = 0;
                        selectedAudiences.forEach((audience) => {
                          aud += audience.value;
                        });
                        surveyTemplateService
                          .addUpdate(id, name, aud, selectedTags, isCheckIn, {
                            name: 'admin',
                            email: '',
                          })
                          .then((res) => {
                            if (res) {
                              setId(res.id);
                              setStatus({
                                open: true,
                                message: 'Template saved successfully',
                                type: 'success',
                              });
                              updateAfterEdit(res);
                            } else {
                              setStatus({
                                open: true,
                                message: 'Error saving template',
                                type: 'error',
                              });
                            }
                          })
                          .finally(() => {
                            setProcessing(false);
                          });
                      }}
                    >
                      Save
                    </AppButton>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Modal>
        )}
      </Box>
    </>
  );
}
